import React, { useState, useEffect } from 'react'
import Head from 'next/head'
import { EventsWrapper } from '../components/events/styles'
import { useRouter } from 'next/router'
import HeroImage from '../../public/events/event-group-friends-glam-celebrating.jpg'
import Hero from '../components/Hero'
import Reviews from '../components/events/Reviews'
import BookEvent from '../components/events/BookEvent'
import HeaderContent from '../components/events/HeaderContent'
import WhyGlamsquad from '../components/WhyGlamsquad'
import ServiceCardList from '../components/ServiceCardList'
import FAQ from '../faq'
import { loadMarkets, fetchEventServicesForMarket } from '../services'
import { useMarketContext } from '../components/MarketContext'
import imageOne from '../../public/events/event-onsite-hair-styling-wwd.jpg'
import imageTwo from '../../public/events/event-glam-makeup-vanity-mirror.jpeg'
import imageThree from '../../public/events/event-shark-beauty-touchup-station.jpeg'
import imageFour from '../../public/events/event-glamsquad-pro-team.jpeg'
import imageFive from '../../public/events/event-makeup-application-getting-ready.jpeg'
import imageSix from '../../public/events/event-kristin-ess-haircare-flatlay.jpeg'
import imageSeven from '../../public/events/event-pink-dessert-party-table.jpeg'
import imageEight from '../../public/events/event-glam-guests-dinner-party.jpeg'
import imageNine from '../../public/events/event-candlelit-dinner-tablescape.jpeg'
import imageTen from '../../public/events/event-makeup-touchup-evening-party.jpeg'
import imageEleven from '../../public/events/event-smirnoff-outdoor-activation.jpeg'
import imageTwelve from '../../public/events/event-jvn-rooftop-beauty-bar.jpeg'
import imageThirteen from '../../public/events/event-griselda-manicure-station.jpg'
import imageFourteen from '../../public/events/event-blowout-station-guest.jpeg'
import imageFifteen from '../../public/events/event-powder-room-makeup.jpeg'
import imageSixteen from '../../public/events/event-makeup-lash-station-greenery.jpeg'
import imageSeventeen from '../../public/events/event-backstage-makeup-artist.jpeg'
import ImageScroll from '../components/ImageScroll'
import { Header2 } from '../styles/Components'

const eventImages = [
  imageOne,
  imageTwo,
  imageThree,
  imageFour,
  imageFive,
  imageSix,
  imageSeven,
  imageEight,
  imageNine,
  imageTen,
  imageEleven,
  imageTwelve,
  imageThirteen,
  imageFourteen,
  imageFifteen,
  imageSixteen,
  imageSeventeen,
]

// Alt text for each carousel image, in the same order as eventImages.
const eventImageAlts = [
  "GLAMSQUAD stylist curling a client's hair at an on-site beauty station during a WWD event",
  'Client with event glam makeup and styled curls at a vanity mirror',
  'GLAMSQUAD x Shark Beauty hair and makeup touch-up station at an event',
  'GLAMSQUAD team of hair and makeup pros at an event glam suite',
  'GLAMSQUAD makeup artist applying eye makeup to a client getting ready',
  'Kristin Ess x GLAMSQUAD styling products and tools on a vanity tray',
  'Pink-themed dessert and drinks table at a GLAMSQUAD event',
  'Two guests with event hair and makeup holding cocktails at a candlelit dinner',
  'Elegant candlelit dinner table setting with roses at a private event',
  'GLAMSQUAD makeup artist applying blush to a guest at an evening event',
  'GLAMSQUAD makeup artists at an outdoor Smirnoff beauty activation tent',
  'GLAMSQUAD pro with guests at a rooftop JVN beauty bar activation',
  'Guest receiving a manicure at a Netflix Griselda event glam station',
  'Guest in a sequined gown at a GLAMSQUAD blowout and glam station',
  'GLAMSQUAD artist applying eye makeup to a guest in an event powder room',
  'GLAMSQUAD makeup artist applying makeup at a flower-decorated event station',
  "GLAMSQUAD makeup artist doing a client's makeup backstage at an event",
]

const FAQ_URL = '/faq'
const CANCELLATION_URL = '/faq'

const FAQ_DATA_EVENTS = [
  {
    id: 1,
    title: 'What services do you provide for events?',
    body: `Glamsquad is experienced coordinating large groups, photoshoots, and corporate events. We can work with you to meet your event's needs. Please email events@glamsquad.com for more information!`,
  },
  {
    id: 2,
    title: 'What is the Event Cancellation Policy?',
    body: `Our cancellation policy is nonrefundable and ensures that our beauty professionals are compensated for their blocked-off time and commitment. This policy supports the hard work and dedication of our professionals, allowing us to maintain a high standard of service for all clients. Please review the detailed terms <a href='${CANCELLATION_URL}' target='_blank' rel='noopener noreferrer'>here</a> for specific timeframes and associated charges, including the non-refundable deposit required for booking.`,
  },
  {
    id: 3,
    title: 'Can I use a promo code when booking an event?',
    body: 'Promo codes are not valid for event or wedding services.',
  },
]

export const getServerSideProps = async () => {
  try {
    const [nyMarket] = await loadMarkets()
    const marketServices = await fetchEventServicesForMarket(nyMarket.id)

    return {
      props: {
        // matches the shape for main services page so component can be re-used
        marketServices: { Events: marketServices },
      },
    }
  } catch (e) {
    console.log(e)
    return {
      props: {
        marketServices: [],
      },
    }
  }
}

const Events = ({ marketServices }) => {
  const router = useRouter()
  const { markets, selectedMarket } = useMarketContext()
  const [services, setServices] = useState(marketServices)

  useEffect(() => {
    if (selectedMarket) {
      fetchEventServicesForMarket(selectedMarket.id).then((s) => {
        setServices({ Events: s })
      })
    }
  }, [selectedMarket])

  const handleClick = () => router.push('/events/contact?origin=events')

  const reasons = [
    {
      title: 'Any event, any size',
      content: `The same salon-quality services you know and love—at scale. There's no event too big or too small for our team to handle.`,
    },
    {
      title: 'Dedicated concierge support',
      content:
        'White glove service every step of the way. Your concierge will help customize your event and match you with the best pros for the job.',
    },
    {
      title: 'Flexible pricing to meet your needs',
      content: `We'll work closely with you to find the best pricing for your needs. (Yes, really.)`,
    },
  ]

  // use users selected market
  // otherwise fallback to the first market in the array
  const marketToDisplay = selectedMarket || (markets?.length && markets[0])

  return (
    <>
      <Head>
        <title>Beauty Event Services | GLAMSQUAD</title>
        <meta name="description" content="Hair, makeup, and nail teams for private parties, corporate functions, and photoshoots—groups of 2 to 500+. Get a custom quote from a dedicated concierge." />
        <link rel="canonical" href="https://glamsquad.com/events" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Beauty Event Services | GLAMSQUAD" />
        <meta property="og:description" content="Hair, makeup, and nail teams for private parties, corporate functions, and photoshoots—groups of 2 to 500+. Get a custom quote from a dedicated concierge." />
        <meta property="og:url" content="https://glamsquad.com/events" />
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: FAQ_DATA_EVENTS.map((q) => ({
              '@type': 'Question',
              name: q.title,
              acceptedAnswer: { '@type': 'Answer', text: q.body },
            })),
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Service',
            name: 'Event Beauty Services',
            serviceType: 'On-location event hair, makeup, and nail services',
            description:
              'On-demand hair, makeup, and nail teams for private parties, corporate functions, and photoshoots—for groups of 2 to 500+.',
            provider: { '@id': 'https://glamsquad.com/#organization' },
            areaServed: { '@type': 'Country', name: 'United States' },
            url: 'https://glamsquad.com/events',
          })}
        </script>
      </Head>
      <EventsWrapper>
        <Hero
          className="hero-component"
          image={HeroImage}
          imageAlt="Group of friends with event-ready hair and makeup laughing together at a party"
          content={<HeaderContent {...{ handleClick }} />}
        />
        <WhyGlamsquad {...{ reasons }} />
        <ServiceCardList
          services={services}
          selectedMarket={marketToDisplay}
          showServiceInfo={false}
          showOnlyBookButton={true}
        />
        <Reviews />
        <div className="dazzling-header">
          <Header2>Make it dazzling</Header2>
        </div>
        <ImageScroll images={eventImages} alts={eventImageAlts} />
        <div className="faq-wrapper">
          <FAQ data={FAQ_DATA_EVENTS} url={FAQ_URL} />
        </div>
        <BookEvent {...{ handleClick }} />
      </EventsWrapper>
    </>
  )
}

export default Events
